import { v4 as uuidv4 } from 'uuid'
import type {
  CoreAppPrivateApi,
  InternalEventType,
  InternalEventListener,
  ModuleServices,
} from '@wix/wix-data-client-common'
import { InterceptorsAPI } from '../interceptors/interceptors-api'
import { get } from 'lodash'
import { openGenericConfirmationPanel } from '@wix/wix-data-client-common'

export default ({
  experimentsManager,
  collectionsTransport,
  hostname,
  sdkVersion,
  transliterateAndSlugify,
  i18n,
  logger,
  finalAppApiPromise,
  getEnterpriseBrandName,
  editorSdkProxy,
  editorType,
  getInstance,
  getGridAppId,
  dataSettingsManager,
  collectionItemCountsManager,
}: ModuleServices): CoreAppPrivateApi => {
  const interceptorsAPI = new InterceptorsAPI()

  const listeners = {} as Record<
    InternalEventType,
    Record<string, InternalEventListener>
  >

  const emit: CoreAppPrivateApi['emit'] = (eventType, value) => {
    if (listeners[eventType]) {
      Object.values(listeners[eventType]).forEach(callback => callback(value))
    }
  }

  const { listSchemas, loadSchema, saveSchema, removeSchema, restoreSchema } =
    collectionsTransport.public

  return {
    getHostname: async () => hostname,

    getSdkVersion: async () => sdkVersion,

    addListener: (eventType, callback) => {
      const listenerToken = uuidv4()

      listeners[eventType] ??= {}

      Object.assign(listeners[eventType], { [listenerToken]: callback })
      return listenerToken
    },

    removeListener: (eventType, token) => {
      if (listeners[eventType]) {
        delete listeners[eventType][token]
      }
    },

    emit,
    updateAppState: async partialState => {
      await editorSdkProxy.editor.updateAppState(partialState)
      emit('appStateUpdated', partialState)
    },

    listSchemas,
    loadSchema,
    saveSchema,
    removeSchema,
    restoreSchema,

    // @ts-expect-error not in interface, since only for tests
    clearSchemasCacheForTests: async () => {
      await collectionsTransport.private.clearCache()
    },

    refreshSchemasCache: async () => {
      await collectionsTransport.private.refreshCache()
      finalAppApiPromise.then(async appApi => {
        try {
          await appApi.refreshControllersState()
        } catch (error) {
          logger.error(
            new Error(
              'Failed to refresh controllers state after cache refresh',
              {
                cause: error,
              },
            ),
          )
        }
      })
    },

    async getAllExperiments() {
      await experimentsManager.ready()
      return experimentsManager.all()
    },

    transliterateAndSlugify: phrase => transliterateAndSlugify(phrase),

    getTranslations: () => {
      return i18n.getResourceBundle(i18n.language, 'translation')
    },

    getEnterpriseBrandName,

    getDeveloperMode: async () =>
      editorSdkProxy.editor.developerMode.isEnabled(),

    registerInterceptor: interceptorsAPI.registerInterceptor,
    unregisterInterceptor: interceptorsAPI.unregisterInterceptor,
    invokeInterceptors: interceptorsAPI.invokeInterceptors,

    getEditorType: async () => editorType,

    showToastNotification: async ({ message, type, linkText }, onLinkClick) =>
      editorSdkProxy.editor.showUserActionNotification({
        message,
        type,
        link: {
          // @ts-expect-error typing
          caption: linkText,
          // @ts-expect-error typing
          onClick: onLinkClick,
        },
      }),

    openErrorPanel: ({
      title,
      text,
      buttonLabel,
    }: { title?: string; text?: string; buttonLabel?: string } = {}) =>
      openGenericConfirmationPanel({
        editorSdkProxy,
        t: i18n.t.bind(i18n),
        logger,
        headerText: title,
        descriptionText: text,
        mainActionText: buttonLabel,
      }).catch(error => {
        logger.error(new Error('Failed to open error panel', { cause: error }))
      }),

    getInstance: async () => getInstance(),
    getGridAppId: async () => getGridAppId(),
    getDataSettings: async () => dataSettingsManager.getDataSettings(),
    updateDataSettings: dataSettingsManager.updateDataSettings,
    getCollectionItemCounts: collectionItemCountsManager.getCounts,
    refreshCollectionItemCounts: collectionItemCountsManager.refreshCounts,
    callEditorSDK: async (method, ...args) => {
      return await (get(editorSdkProxy, method) as any)(...args)
    },
  }
}
